import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Avatar, Tabs, Tab,
  Snackbar, Alert, IconButton, Collapse
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAccount, usePublicClient } from 'wagmi';
import { erc20Abi as ERC20_ABI, formatUnits } from 'viem';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../components/firebase';
import useCryptoMiles from '../../hooks/useCryptoMiles';


import StakeCard from '../components/StakeCard';
import SwapCard from '../components/SwapCard';

/* ======== Black Glass  ======== */
const R12 = '12px';
const GLASS = {
  heroBg:    'linear-gradient(180deg, rgba(13,17,23,.94) 0%, rgba(13,17,23,.86) 100%)',
  surfaceBg: 'linear-gradient(180deg, rgba(17,22,30,.92) 0%, rgba(17,22,30,.82) 70%)',
  tileBg:    'rgba(21,27,38,.60)',
  border:    '1px solid rgba(255,255,255,.14)',
  text:      '#eef1f6',
  subtext:   '#b8c0cc',
  indicator: '#14F195',
};

const PRUSD = { symbol: 'prUSD', decimals: 6 };
const LOGO = {
  prUSD: 'https://assets.coingecko.com/coins/images/6319/standard/usdc.png?1696506694',
};
const PRUSD_TOKEN_ADDRESS = null;

/* ---------------------------- Collapsible HowItWorks ---------------------------- */
function HowItWorksCard() {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const [open, setOpen] = useState(isSmUp); 

  return (
    <Card
      sx={{
        mt: 1.25, maxWidth: 900, mx: 'auto', borderRadius: R12,
        background: GLASS.surfaceBg, color: GLASS.text,
        border: GLASS.border, backdropFilter: 'blur(10px)',
        boxShadow: '0 12px 32px rgba(2,8,23,.45)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: { xs: 1.25, md: 2 }, py: { xs: .75, md: 1.25 }
        }}
      >
        <InfoOutlinedIcon sx={{ opacity: 0.9 }} />
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 900, flex: 1, fontSize: { xs: '.9rem', sm: '1rem' } }}
        >
          How staking works
        </Typography>
        <IconButton
          size="small"
          onClick={() => setOpen(o => !o)}
          sx={{
            transform: `rotate(${open ? 180 : 0}deg)`,
            transition: 'transform .2s ease', color: GLASS.text
          }}
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <CardContent sx={{ pt: 0, px: { xs: 1.25, md: 2 }, pb: { xs: 1.25, md: 2 } }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
            gap: 1, mb: 1,
          }}>
            <Step
              title="1) Stake"
              text="Select a stablecoin (USDT/USDC/DAI/USDe), enter the amount and click Stake."
            />
            <Step
              title="2) Get prUSD"
              text="We convert your stablecoin to prUSD at a 1:1 rate minus a commission."
            />
            <Step
              title="3) Unstake / Withdraw"
              text="When you Unstake, you give up prUSD and get the chosen stablecoin back (%)."
            />
          </Box>

          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, p: 1,
            borderRadius: R12, background: GLASS.tileBg, border: GLASS.border,
          }}>
            <EmojiEventsOutlinedIcon />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: { xs: '.8rem', sm: '.9rem' } }}>
                Rewards
              </Typography>
              <Typography variant="body2" sx={{ color: GLASS.subtext, fontSize: { xs: '.75rem', sm: '.875rem' } }}>
                Accrued <strong>Crypto miles</strong> for activity — they can be exchanged for prUSD.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
}

function Step({ title, text }) {
  return (
    <Box
      sx={{
        p: { xs: .75, sm: 1 }, borderRadius: R12, border: GLASS.border,
        background: GLASS.tileBg, minHeight: { xs: 72, sm: 96 },
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: .5, fontSize: { xs: '.85rem', sm: '.9rem' } }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: GLASS.subtext, fontSize: { xs: '.75rem', sm: '.875rem' } }}>
        {text}
      </Typography>
    </Box>
  );
}

/* ---------------------------- Page ---------------------------- */
export default function StakePage() {
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const [user] = useAuthState(auth);
  const { total: milesTotal, pending: milesPending, loading: milesLoading } = useCryptoMiles(user?.uid);

  const [tab, setTab] = useState('stake'); // 'stake' | 'swap'
  const [snack, setSnack] = useState('');
  const [prUSDBal, setPrUSDBal] = useState(0n);

  useEffect(() => {
    let stop = false;
    (async () => {
      if (!publicClient || !address || !PRUSD_TOKEN_ADDRESS) return;
      try {
        const bal = await publicClient.readContract({
          address: PRUSD_TOKEN_ADDRESS, abi: ERC20_ABI, functionName: 'balanceOf', args: [address]
        });
        if (!stop) setPrUSDBal(bal);
      } catch {}
    })();
    return () => { stop = true; };
  }, [publicClient, address]);

  return (
    
    <Box sx={{ minHeight: '100vh', px: { xs: 1, md: 2 }, py: { xs: 1, md: 2 } }}>
      {/* HERO */}
      <Card
        sx={{
          mb: 1, borderRadius: R12,
          background: GLASS.heroBg, color: GLASS.text,
          border: GLASS.border, backdropFilter: 'blur(10px)',
          boxShadow: '0 12px 32px rgba(2,8,23,.45)',
        }}
      >
        <CardContent sx={{ p: { xs: .75, md: 2 }, textAlign: 'center' }}>
          <Typography
            variant="h6"
            align="center"
            sx={{
              fontFamily: "'Bruno Ace SC', sans-serif",
              fontSize: { xs: '.9rem', sm: '1.25rem' }, 
              lineHeight: 1.2
            }}
          >
            Provide liquidity and make a profit.
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{ color: GLASS.subtext, mt: { xs: .25, sm: .5 }, fontSize: { xs: '.75rem', sm: '.875rem' } }}
          >
            Use staking and get your first {PRUSD.symbol} now + CrM
          </Typography>
        </CardContent>
      </Card>

      {/* Balances - ON MOBILE IN ONE ROW (2 columns) + font compression/indents*/}
      <Card
        sx={{
          mb: 1, borderRadius: R12,
          background: GLASS.surfaceBg, color: GLASS.text,
          border: GLASS.border, backdropFilter: 'blur(10px)',
          boxShadow: '0 12px 32px rgba(2,8,23,.45)',
        }}
      >
        <CardContent sx={{ p: { xs: 1, md: 2 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr' }, 
              gap: { xs: .75, sm: 1 },
              alignItems: 'stretch'
            }}
          >
            <Box sx={{ p: { xs: .75, sm: 1 }, borderRadius: R12, border: GLASS.border, background: GLASS.tileBg, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
                <Avatar src={LOGO.prUSD} sx={{ width: 18, height: 18 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: { xs: '.8rem', sm: '.9rem' } }}>
                  Balance
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{ mt: .25, fontWeight: 900, fontSize: { xs: '.95rem', sm: '1.1rem' } }}
              >
                {PRUSD_TOKEN_ADDRESS ? `${formatUnits(prUSDBal, PRUSD.decimals)} ${PRUSD.symbol}` : `0 ${PRUSD.symbol}`}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: GLASS.subtext, fontSize: { xs: '.7rem', sm: '.75rem' } }}
              >
                Total balance of {PRUSD.symbol}
              </Typography>
            </Box>

            <Box sx={{ p: { xs: .75, sm: 1 }, borderRadius: R12, border: GLASS.border, background: GLASS.tileBg, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
                <Avatar sx={{ width: 18, height: 18 }}>★</Avatar>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: { xs: '.8rem', sm: '.9rem' } }}>
                  Rewards Balance
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{ mt: .25, fontWeight: 900, fontSize: { xs: '.95rem', sm: '1.1rem' } }}
              >
                {milesLoading ? '—' : `${milesTotal} CrM`}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: GLASS.subtext, fontSize: { xs: '.7rem', sm: '.75rem' } }}
              >
                Crypto miles → {PRUSD.symbol}{milesPending ? ` • Pending: ${milesPending}` : '' }
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* tabs*/}
      <Box sx={{ maxWidth: 560, mx: 'auto', mb: 1 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 36, height: 36,
            '& .MuiTab-root': {
              minHeight: 36, height: 36, textTransform: 'none', fontWeight: 900,
              fontSize: { xs: '.85rem', sm: '1rem' }, color: '#0b0f15'
            },
            '& .Mui-selected': { color: '#0b0f15 !important' },
            '& .MuiTabs-indicator': { backgroundColor: GLASS.indicator },
          }}
        >
          <Tab value="stake" label="Stake" />
          <Tab value="swap"  label="Swap" />
        </Tabs>
      </Box>

      {/* content */}
      {tab === 'stake' ? <StakeCard onNotify={setSnack} /> : <SwapCard onNotify={setSnack} />}

      {/* How it works —  */}
      <HowItWorksCard />

      {/* Snackbar */}
      <Snackbar open={!!snack} autoHideDuration={3200} onClose={() => setSnack('')}>
        <Alert
          onClose={() => setSnack('')}
          severity="info"
          variant="filled"
          sx={{
            borderRadius: R12,
            background: 'rgba(17,22,30,.92)',
            color: GLASS.text,
            border: GLASS.border,
            backdropFilter: 'blur(6px)'
          }}
        >
          {snack}
        </Alert>
      </Snackbar>
    </Box>
  );
}
